export interface AutomatedRuleRecord {
  id: string;
  ruleId: string;
  name: string;
  audience: string;
  triggerType: string;
  scheduleType: string;
  templateKey: string;
  conditionKey?: string;
  cronExpression?: string;
  delayMinutes?: number;
  windowHour?: number;
  windowDayOfWeek?: string;
  cooldownDays?: number;
  priority: string;
  nextRun?: string;
  status: string;
  lastEnqueueResult?: string;
  lastFiredAt?: string;
  updatedAt?: string;
  createdAt?: string;
}

export enum ActionTypes {
  FETCH_RULES_REQUEST  = 'automatedEmails/fetchRequest',
  FETCH_RULES_SUCCESS  = 'automatedEmails/fetchSuccess',
  FETCH_RULES_FAILURE  = 'automatedEmails/fetchFailure',

  UPDATE_RULE_REQUEST  = 'automatedEmails/updateRequest',
  UPDATE_RULE_SUCCESS  = 'automatedEmails/updateSuccess',
  UPDATE_RULE_FAILURE  = 'automatedEmails/updateFailure',

  TOGGLE_STATUS_REQUEST = 'automatedEmails/toggleStatusRequest',
  TOGGLE_STATUS_SUCCESS = 'automatedEmails/toggleStatusSuccess',
  TOGGLE_STATUS_FAILURE = 'automatedEmails/toggleStatusFailure',
}

// ── Fetch ─────────────────────────────────────────────────────────────────────

export type FetchRulesRequestAction = {
  type: ActionTypes.FETCH_RULES_REQUEST;
  payload: { page: number; size: number; audience?: string; status?: string };
}
export type FetchRulesSuccessAction = {
  type: ActionTypes.FETCH_RULES_SUCCESS;
  payload: { rules: AutomatedRuleRecord[]; totalElements: number };
}
export type FetchRulesFailureAction = {
  type: ActionTypes.FETCH_RULES_FAILURE;
  payload: string;
}

export const fetchRulesRequest = (
  page = 0, size = 200, audience?: string, status?: string
): FetchRulesRequestAction => ({
  type: ActionTypes.FETCH_RULES_REQUEST,
  payload: { page, size, audience, status },
});

export const fetchRulesSuccess = (
  rules: AutomatedRuleRecord[], totalElements: number
): FetchRulesSuccessAction => ({
  type: ActionTypes.FETCH_RULES_SUCCESS,
  payload: { rules, totalElements },
});

export const fetchRulesFailure = (error: string): FetchRulesFailureAction => ({
  type: ActionTypes.FETCH_RULES_FAILURE,
  payload: error,
});

// ── Update rule ───────────────────────────────────────────────────────────────

export type UpdateRuleRequestAction = {
  type: ActionTypes.UPDATE_RULE_REQUEST;
  payload: { id: string; updates: Partial<AutomatedRuleRecord> };
}
export type UpdateRuleSuccessAction = {
  type: ActionTypes.UPDATE_RULE_SUCCESS;
  payload: AutomatedRuleRecord;
}
export type UpdateRuleFailureAction = {
  type: ActionTypes.UPDATE_RULE_FAILURE;
  payload: string;
}

export const updateRuleRequest = (
  id: string, updates: Partial<AutomatedRuleRecord>
): UpdateRuleRequestAction => ({
  type: ActionTypes.UPDATE_RULE_REQUEST,
  payload: { id, updates },
});
export const updateRuleSuccess = (rule: AutomatedRuleRecord): UpdateRuleSuccessAction => ({
  type: ActionTypes.UPDATE_RULE_SUCCESS,
  payload: rule,
});
export const updateRuleFailure = (error: string): UpdateRuleFailureAction => ({
  type: ActionTypes.UPDATE_RULE_FAILURE,
  payload: error,
});

// ── Toggle status (pause / resume) ───────────────────────────────────────────

export type ToggleStatusRequestAction = {
  type: ActionTypes.TOGGLE_STATUS_REQUEST;
  payload: { id: string; currentStatus: string };
}
export type ToggleStatusSuccessAction = {
  type: ActionTypes.TOGGLE_STATUS_SUCCESS;
  payload: AutomatedRuleRecord;
}
export type ToggleStatusFailureAction = {
  type: ActionTypes.TOGGLE_STATUS_FAILURE;
  payload: string;
}

export const toggleStatusRequest = (
  id: string, currentStatus: string
): ToggleStatusRequestAction => ({
  type: ActionTypes.TOGGLE_STATUS_REQUEST,
  payload: { id, currentStatus },
});
export const toggleStatusSuccess = (rule: AutomatedRuleRecord): ToggleStatusSuccessAction => ({
  type: ActionTypes.TOGGLE_STATUS_SUCCESS,
  payload: rule,
});
export const toggleStatusFailure = (error: string): ToggleStatusFailureAction => ({
  type: ActionTypes.TOGGLE_STATUS_FAILURE,
  payload: error,
});

export type AutomatedEmailsAction =
  | FetchRulesRequestAction | FetchRulesSuccessAction | FetchRulesFailureAction
  | UpdateRuleRequestAction | UpdateRuleSuccessAction | UpdateRuleFailureAction
  | ToggleStatusRequestAction | ToggleStatusSuccessAction | ToggleStatusFailureAction;
